import React, { useState } from "react";
import { useKitchenEta } from "../../hooks/useKitchenEta";
import KitchenEtaBadge from "../Kitchen/KitchenEtaBadge";

const STATUS_RANK = { new: 0, preparing: 1, ready: 2, served: 3 };

// One table can have several customer groups: show the one still waiting longest
const tableEta = (table, etas) =>
  (table.currentAllocations || [])
    .filter((a) => a.type === "allocation")
    .map((a) => etas.tables[String(a.id)])
    .filter(Boolean)
    .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || b.waitMin - a.waitMin)[0];

const TableGrid = ({ tables, handleToggleTable, onTableClick }) => {
  const [expandedId, setExpandedId] = useState(null);
  const etas = useKitchenEta({
    allocationIds: tables.flatMap((t) =>
      (t.currentAllocations || []).filter((a) => a.type === "allocation").map((a) => a.id),
    ),
  });

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="mt-6 h-[55vh] overflow-y-auto pr-2">
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
        {tables.map((t) => {
          let bgColor = "bg-white";
          let borderColor = "border-gray-300";
          let textColor = "text-gray-800";

          if (t.status === "available") {
            bgColor = "bg-blue-50";
            borderColor = "border-blue-400";
            textColor = "text-blue-900";
          }

          if (t.status === "reserved") {
            bgColor = "bg-orange-50";
            borderColor = "border-orange-500";
            textColor = "text-orange-900";
          }

          if (t.status === "occupied") {
            bgColor = "bg-green-50";
            borderColor = "border-green-400";
            textColor = "text-green-900";
          }

          const totalCustomers = t.currentAllocations?.length || 0;
          const isInactive = !t.isActive;

          return (
            <div
              key={t._id}
              className={`p-5 text-center rounded-2xl border-2 shadow-lg relative transition-all ${
                isInactive
                  ? "bg-gray-100 border-gray-400 text-gray-500 opacity-60"
                  : `${bgColor} ${borderColor} ${textColor}`
              } ${
                t.status === "occupied"
                  ? "cursor-pointer hover:shadow-xl"
                  : "cursor-default"
              }`}
              onClick={() => {
                if (!t.isActive) return;
                if (t.status === "available" || t.status === "occupied") {
                  onTableClick(t);
                }
              }}
            >
              {t.status === "occupied" && totalCustomers > 0 && (
                <div className="absolute top-2 right-2 bg-red-600 text-white text-sm font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-md">
                  {totalCustomers}
                </div>
              )}

              <h3 className="m-0 text-xl font-bold">{t.tableNumber}</h3>

              <p className="mt-2.5 text-base font-medium">
                Capacity: {t.capacity}
              </p>

              <p className="mt-2 font-semibold text-sm">
                {t.status.toUpperCase()}
              </p>

              {t.status === "occupied" && (
                <KitchenEtaBadge eta={tableEta(t, etas)} className="mt-2" />
              )}

              {t.status === "available" && t.isActive && (
                <>
                  <button
                    className="mt-2 px-4 py-1.5 text-sm border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(t._id);
                    }}
                  >
                    Manage Table
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      expandedId === t._id ? "max-h-20 mt-2" : "max-h-0"
                    }`}
                  >
                    <button
                      className="w-full px-4 py-2 text-sm bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleTable(t._id);
                      }}
                    >
                      Disable Table
                    </button>
                  </div>
                </>
              )}

              {t.status === "available" && !t.isActive && (
                <button
                  className="mt-4 w-full px-4 py-2 text-sm bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleTable(t._id);
                  }}
                >
                  Enable Table
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TableGrid;
